import { Activity } from "@outschool/gql-backend-generated";
import {
  CurrentUserSavedActivitiesQuery,
  CurrentUserSavedActivitiesQueryVariables,
  ListCurrentUserSavedClassListsQuery,
  ListCurrentUserSavedClassListsQueryVariables,
  ListSavedClassesWithPhotosQuery,
  ListSavedClassesWithPhotosQueryVariables,
  SavedClassListQueryQuery,
  SavedClassListQueryQueryVariables,
} from "@outschool/gql-frontend-generated";
import { QueryResult, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { updateQuery } from "@outschool/ui-gql";
import ldKeyBy from "lodash/keyBy";
import React, { useCallback } from "react";

import {
  CurrentUserSavedActivitiesQueryStr,
  ListCurrentUserSavedClassLists,
  ListSavedClassesWithPhotos,
  SavedClassListQuery,
} from "./queries";

export function useSavedActivities() {
  const { isLoggedIn } = useSession();
  const { data } = useQueryWithPreviousData<
    CurrentUserSavedActivitiesQuery,
    CurrentUserSavedActivitiesQueryVariables
  >(CurrentUserSavedActivitiesQueryStr, {
    skip: !isLoggedIn,
  });
  return data?.currentUser?.savedActivities ?? [];
}

function useSavedActivitiesByUid() {
  const savedActivities = useSavedActivities();
  return React.useMemo(
    () => ldKeyBy(savedActivities, "activity_uid"),
    [savedActivities]
  );
}

export function useIsActivitySavedByCurrentUser(
  activity: Pick<Activity, "uid">
) {
  const savedActivitiesByUid = useSavedActivitiesByUid();
  return React.useMemo(
    () => Boolean(savedActivitiesByUid[activity?.uid]),
    [activity?.uid, savedActivitiesByUid]
  );
}

export function useSavedActivitiesCount() {
  const savedActivities = useSavedActivities();
  return React.useMemo(() => savedActivities.length, [savedActivities]);
}

// Explicit typing required to prevent TS2742
type SavedClassListResult = Omit<
  QueryResult<SavedClassListQueryQuery, SavedClassListQueryQueryVariables>,
  "fetchMore"
> & {
  listings: NonNullable<
    NonNullable<SavedClassListQueryQuery["savedClassList"]>["listings"]
  >;
  fetchMore: (offset: number) => Promise<void>;
};

export function useSavedClassList(
  listUid: string | undefined,
  searchFilters: any,
  limit: number,
  offset: number,
  showListsForUserAndLearners: boolean
): SavedClassListResult {
  const { fetchMore, ...result } = useQueryWithPreviousData<
    SavedClassListQueryQuery,
    SavedClassListQueryQueryVariables
  >(SavedClassListQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      listUid,
      showListsForUserAndLearners: !listUid || showListsForUserAndLearners,
      searchFilters,
      skipRating: false,
      skipSavedActivities: true,
      offset,
      limit,
    },
  });
  const wrappedFetchMore = useCallback(
    async (offset: number) => {
      await fetchMore({
        variables: { offset },
        updateQuery: updateQuery<any, any>("savedClassList.listings"),
      });
    },
    [fetchMore]
  );
  return {
    ...result,
    listings: result?.data?.savedClassList?.listings ?? [],
    fetchMore: wrappedFetchMore,
  };
}

export function useListCurrentUserSavedClassLists() {
  const { loading, data } = useQueryWithPreviousData<
    ListCurrentUserSavedClassListsQuery,
    ListCurrentUserSavedClassListsQueryVariables
  >(ListCurrentUserSavedClassLists);
  return { loading, savedClassLists: data?.currentUser?.savedClassLists ?? [] };
}

export function useListSavedClassesWithPhotos() {
  return useQueryWithPreviousData<
    ListSavedClassesWithPhotosQuery,
    ListSavedClassesWithPhotosQueryVariables
  >(ListSavedClassesWithPhotos);
}
